import { useCallback, useEffect, useMemo, useState } from "react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { useTranslation } from "@/language"
import { getAssetUrl } from "@/utils/assets"
import { useCreateSocialPost } from "../hooks/useCreateSocialPost"
import { useFacebookPages } from "../hooks/useFacebookPages"
import { useSocialPostStatus } from "../hooks/useSocialPostStatus"
import type {
  FacebookPageInfo,
  ShareContext,
  SocialPostProvider,
} from "../types/social-publish.types"

const MAX_CAPTION_LENGTH = 2200

interface InstagramShareDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  shareContext: ShareContext
}

function getInitialCaption(ctx: ShareContext): string {
  switch (ctx.type) {
    case "ritual":
      return ctx.description ? ctx.description.slice(0, MAX_CAPTION_LENGTH) : ctx.title
    case "coaching":
      return ctx.title
    case "community":
      return ctx.name
    case "profile":
      return ctx.title
  }
}

function getRitualId(ctx: ShareContext): string | null {
  if (ctx.type === "ritual") return ctx.ritualId
  return null
}

function getLinkUrl(ctx: ShareContext): string | null {
  switch (ctx.type) {
    case "ritual":
      return null
    case "coaching":
      return `/coaching/${ctx.coachingId}`
    case "community":
      return `/communities/${ctx.communityId}`
    case "profile":
      return null
  }
}

function getImageUrl(ctx: ShareContext): string {
  const raw = (() => {
    switch (ctx.type) {
      case "ritual":
        return ctx.imageUrl
      case "coaching":
        return ctx.imageUrl
      case "community":
        return ctx.imageUrl
      case "profile":
        return ctx.imageUrl
    }
  })()
  // blob: URLs are local to the browser and can't be fetched by Instagram
  if (!raw || raw.startsWith("blob:")) return getAssetUrl("rituality-og.png")
  return raw
}

export function InstagramShareDialog({
  open,
  onOpenChange,
  shareContext,
}: InstagramShareDialogProps) {
  const { t } = useTranslation("common")
  const [selectedPageId, setSelectedPageId] = useState<string>("")
  const [caption, setCaption] = useState("")
  const [postId, setPostId] = useState<string | null>(null)

  const initialCaption = useMemo(() => getInitialCaption(shareContext), [shareContext])
  const ritualId = useMemo(() => getRitualId(shareContext), [shareContext])
  const linkUrl = useMemo(() => getLinkUrl(shareContext), [shareContext])
  const rawImageUrl = useMemo(() => getImageUrl(shareContext), [shareContext])
  // Instagram Graph API requires a publicly accessible HTTPS URL
  const imageUrl = useMemo(() => {
    const baseUrl = import.meta.env.VITE_APP_URL || window.location.origin
    const baseHttps = baseUrl.startsWith("http:") ? baseUrl.replace("http:", "https:") : baseUrl
    if (!rawImageUrl) return `${baseHttps}/rituality-og.png`
    if (rawImageUrl.startsWith("http")) return rawImageUrl
    if (rawImageUrl.startsWith("/")) return `${baseHttps}${rawImageUrl}`
    return `${baseHttps}/${rawImageUrl}`
  }, [rawImageUrl])

  const { data: pages, isLoading: pagesLoading } = useFacebookPages(open)
  const createPost = useCreateSocialPost()
  const postStatus = useSocialPostStatus(postId)

  useEffect(() => {
    if (open) {
      setCaption(initialCaption)
      setPostId(null)
    }
  }, [open, initialCaption])

  // Auto-select first page that has an IG business account
  useEffect(() => {
    if (pages && pages.length > 0 && !selectedPageId) {
      const igPage = pages.find(p => !!p.igBusinessAccountId) || pages[0]
      setSelectedPageId(igPage.pageId)
    }
  }, [pages, selectedPageId])

  const selectedPage: FacebookPageInfo | undefined = pages?.find(p => p.pageId === selectedPageId)
  const hasIgBusiness = !!selectedPage?.igBusinessAccountId

  const isPublishing = postId !== null
  const isTerminal =
    postStatus.timedOut ||
    postStatus.data?.status === "published" ||
    postStatus.data?.status === "failed"
  const allDone = isPublishing && isTerminal

  useEffect(() => {
    if (!allDone) return
    if (postStatus.data?.status === "published") {
      toast.success("Published to Instagram!")
    }
    if (postStatus.data?.status === "failed") {
      toast.error(postStatus.data.lastErrorMessage || "Failed to publish. Please try again.")
    }
  }, [allDone, postStatus.data])

  const canPublish = !isPublishing && caption.trim().length > 0 && hasIgBusiness

  const handlePublish = useCallback(async () => {
    if (!canPublish || !selectedPage?.igBusinessAccountId) return

    try {
      const result = await createPost.mutateAsync({
        data: {
          provider: "instagram_business" as SocialPostProvider,
          providerAccountId: selectedPage.igBusinessAccountId,
          caption,
          imageUrl: imageUrl || null,
          linkUrl,
          ritualId,
        },
        idempotencyKey: crypto.randomUUID(),
      })
      setPostId(result.id)
    } catch {
      toast.error("Failed to publish. Please try again.")
    }
  }, [canPublish, selectedPage, caption, imageUrl, linkUrl, ritualId, createPost])

  const handleClose = () => {
    if (createPost.isPending) return
    if (!isPublishing || allDone) {
      setPostId(null)
      setSelectedPageId("")
      onOpenChange(false)
    }
  }

  const noPages = !pagesLoading && (!pages || pages.length === 0)

  return (
    <Dialog open={open} onOpenChange={o => !o && handleClose()}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>{t("instagram.title")}</DialogTitle>
          <DialogDescription>
            Publish to your connected Instagram Business account.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-4">
          {noPages && (
            <div className="text-sm text-stone text-center py-4">
              No Instagram Business account connected.
              <Button
                variant="link"
                className="ml-1 p-0 h-auto text-brand-terracotta"
                onClick={() => {
                  onOpenChange(false)
                  window.location.href = "/settings?section=connected-accounts"
                }}
              >
                Connect Instagram
              </Button>
            </div>
          )}

          {!noPages && (
            <>
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-charcoal">
                  {t("instagram.connectedPage")}
                </label>
                <Select value={selectedPageId} onValueChange={setSelectedPageId}>
                  <SelectTrigger>
                    <SelectValue placeholder={t("instagram.selectPage")} />
                  </SelectTrigger>
                  <SelectContent>
                    {pages?.map(page => (
                      <SelectItem key={page.pageId} value={page.pageId}>
                        <span className="flex items-center gap-2">
                          {page.pageName || page.pageId}
                          {page.igBusinessAccountId && (
                            <span className="text-xs text-stone">
                              + @{page.igBusinessUsername || "IG"}
                            </span>
                          )}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {!hasIgBusiness && (
                <p className="text-xs text-red-500">
                  This page has no linked Instagram Business account.
                </p>
              )}

              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-charcoal">
                  {t("instagram.caption")}
                </label>
                <Textarea
                  value={caption}
                  onChange={e => setCaption(e.target.value.slice(0, MAX_CAPTION_LENGTH))}
                  placeholder={t("instagram.captionPlaceholder")}
                  rows={4}
                  disabled={isPublishing}
                  aria-describedby="caption-counter"
                  style={{ color: "var(--color-text-primary)" }}
                />
                <span id="caption-counter" className="text-xs text-stone text-right">
                  {caption.length}/{MAX_CAPTION_LENGTH}
                </span>
              </div>

              {isPublishing && (
                <div className="flex flex-col gap-2" aria-live="polite">
                  <StatusRow
                    status={postStatus.timedOut ? "timed_out" : postStatus.data?.status || "queued"}
                    permalink={postStatus.data?.providerPermalink}
                  />
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} className="w-[180px]">
            {allDone ? t("instagram.done") : t("instagram.cancel")}
          </Button>
          {!noPages && !allDone && (
            <Button
              variant="primary"
              onClick={handlePublish}
              disabled={!canPublish || createPost.isPending}
              className="w-[180px]"
            >
              {isPublishing
                ? t("instagram.publishing")
                : createPost.isPending
                  ? t("instagram.publishing")
                  : t("instagram.publish")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function StatusRow({ status, permalink }: { status: string; permalink?: string | null }) {
  const { t } = useTranslation("common")
  const statusText =
    status === "published"
      ? t("instagram.published")
      : status === "failed"
        ? t("instagram.failed")
        : status === "timed_out"
          ? t("instagram.timedOut")
          : t("instagram.publishing")

  const statusColor =
    status === "published"
      ? "text-green-600"
      : status === "failed" || status === "timed_out"
        ? "text-red-500"
        : "text-stone"

  return (
    <div className="flex items-center justify-between text-sm">
      <span className="font-medium text-charcoal">{t("instagram.instagram")}</span>
      <span className={`flex items-center gap-2 ${statusColor}`}>
        {statusText}
        {status === "published" && permalink && (
          <a
            href={permalink}
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-terracotta underline"
          >
            View post
          </a>
        )}
      </span>
    </div>
  )
}
